import { Text } from '@thanx/uikit/text'
import { useGetSegmentQuery } from 'api/segments'
import Spinner from 'components/Spinner'
import moment from 'moment-timezone'
import React from 'react'
import { ContentBlockData } from 'slices/appLandingPageTheme/types'
import { useUserTimeZone } from 'utilities/userTimeZone'

type Props = {
  contentBlock: ContentBlockData
}

const ConfigurationInfo = ({ contentBlock }: Props) => {
  const merchantTimezone = useUserTimeZone()

  // Get segment ID from either format
  const segmentId =
    ('target_segments' in contentBlock && contentBlock.target_segments?.[0]) ||
    ('segment_id' in contentBlock && contentBlock.segment_id) ||
    null

  // Fetch selected segment details with RTK Query
  const { data: segmentResponse, isFetching: isSegmentFetching } =
    useGetSegmentQuery(
      { segmentId: segmentId?.toString() || '' },
      {
        skip: !segmentId,
      }
    )

  const selectedSegment = segmentResponse?.target_segment

  // Check for target segments in both backend format (target_segments) and UI format (audience_type + segment_id)
  const hasTargetSegments = Boolean(segmentId)

  // Check for date availability
  const hasDateRange =
    ('start_at' in contentBlock && contentBlock.start_at) ||
    ('end_at' in contentBlock && contentBlock.end_at)

  // Format date range text
  const formatDateTime = (date: string, time?: string) => {
    const timeString = time || '12:00'
    const combinedDateTime = `${date} ${timeString}`
    const dateTimeInMerchantTz = moment.tz(
      combinedDateTime,
      'YYYY-MM-DD HH:mm',
      merchantTimezone
    )
    const formattedDate = dateTimeInMerchantTz.format('MM/DD/YYYY')
    const formattedTime = dateTimeInMerchantTz.format('h:mm A z')
    return `${formattedDate} ${formattedTime}`
  }

  const formatDate = (dateValue: string, timeValue?: string) => {
    // Handle backend format (ISO string)
    if (dateValue.includes('T')) {
      return moment
        .utc(dateValue)
        .tz(merchantTimezone)
        .format('MM/DD/YYYY h:mm A z')
    }
    // Handle UI format (date + time)
    return formatDateTime(dateValue, timeValue)
  }

  const getDateRangeText = () => {
    const startAt = 'start_at' in contentBlock && contentBlock.start_at
    const endAt = 'end_at' in contentBlock && contentBlock.end_at

    if (startAt && endAt) {
      return `${formatDate(startAt)} - ${formatDate(endAt)}`
    } else if (startAt) {
      return `Starting ${formatDate(startAt)}`
    } else if (endAt) {
      return `Until ${formatDate(endAt)}`
    }
    return 'Custom date range'
  }

  return (
    <div className="mt-s">
      {/* Audience information */}
      {hasTargetSegments && (
        <div className="mb-s">
          <Text.BodyText4 tag="p" color="grey90" bold className="mb-xs">
            Audience
          </Text.BodyText4>
          <Text.BodyText4 tag="p" color="grey70">
            {isSegmentFetching ? (
              <Spinner isLoading className="inline-block" />
            ) : (
              selectedSegment?.name || 'Targeted to specific customer segment'
            )}
          </Text.BodyText4>
        </div>
      )}

      {/* Availability information */}
      {hasDateRange && (
        <div className="mb-s">
          <Text.BodyText4 tag="p" color="grey90" bold className="mb-xs">
            Available in app
          </Text.BodyText4>
          <Text.BodyText4 tag="p" color="grey70">
            {getDateRangeText()}
          </Text.BodyText4>
        </div>
      )}
    </div>
  )
}

export default ConfigurationInfo
